import {
  azureStorageChipValue,
  azureStorageOperandTags,
  azureStorageTypedValue,
  type AzureStorageChipCondition,
  type AzureStorageOperandTags,
} from '../../../../../core/engines/azureStorageOData';
import type { StorageEntityValue } from '../../../../../types/azureStorage';
import type { FilterCondition, FilterGroup, StorageExplorerEntityView } from '../../../core/types/workspaceTypes';

/**
 * Session-file representation of entity filter chips.
 *
 * S4 review B, R1: renderer chip state keeps Edm.Int64 as a native `bigint`,
 * which `JSON.stringify` cannot write — the guarded persist storage dropped the
 * write and EVERY tab stopped being saved.
 *
 * S4 review C, M1: a restored tab has NO observed property types until a page
 * loads (and an empty or wrong result never supplies them), so every tagged
 * operand is written as its typed, JSON-safe IPC value `{ t, v }` and restored
 * with its tag — Int32 vs integer-valued Double, Guid vs String, exact
 * seven-digit DateTime text. An untagged legacy operand keeps the plain scalar
 * shape (Int64 still as `{ t: 'int64', v }`).
 */
export type PersistedTypedOperand = StorageEntityValue;
export type PersistedInt64 = { t: 'int64'; v: string };
export type PersistedEntityFilterScalar = string | number | boolean | null | PersistedTypedOperand;
export type PersistedEntityFilterCondition = {
  operator: FilterCondition['operator'];
  value?: PersistedEntityFilterScalar | PersistedEntityFilterScalar[];
  value2?: PersistedEntityFilterScalar;
};
export type PersistedEntityFilterGroup = { field: string; conditions: PersistedEntityFilterCondition[] };
export type SerializedStorageEntityView = Omit<StorageExplorerEntityView, 'filters'> & { filters: PersistedEntityFilterGroup[] };

const EDM_TAGS: ReadonlySet<string> = new Set(['string', 'int32', 'int64', 'double', 'boolean', 'datetime', 'guid', 'binary']);

function encodeScalar(value: unknown, tag: StorageEntityValue['t'] | undefined): PersistedEntityFilterScalar {
  if (tag) {
    // Never let a session write throw: an operand its tag cannot type is
    // written untagged (the conversion reports it when the tab queries).
    try { return azureStorageTypedValue(value, tag); } catch { /* untagged below */ }
  }
  if (typeof value === 'bigint') return { t: 'int64', v: value.toString() };
  if (value instanceof Date) return value.toISOString();
  if (value === null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  return String(value);
}

function encodeCondition(condition: FilterCondition): PersistedEntityFilterCondition {
  const tags = azureStorageOperandTags(condition);
  const listTags = Array.isArray(tags?.value) ? tags.value : [];
  const scalarTag = Array.isArray(tags?.value) ? undefined : tags?.value;
  return {
    operator: condition.operator,
    ...(condition.value !== undefined
      ? { value: Array.isArray(condition.value) ? condition.value.map((item, index) => encodeScalar(item, listTags[index])) : encodeScalar(condition.value, scalarTag) }
      : {}),
    ...(condition.value2 !== undefined ? { value2: encodeScalar(condition.value2, tags?.value2) } : {}),
  };
}

export const encodeEntityFiltersForSession = (filters: readonly FilterGroup[]): PersistedEntityFilterGroup[] =>
  filters.map((group) => ({ field: group.field, conditions: group.conditions.map(encodeCondition) }));

/** The explicit `entityView` whitelist (filters, odataFilter, filterMode, selectedColumns, pageSize), JSON-safe. */
export const serializeEntityViewForSession = (view: StorageExplorerEntityView): SerializedStorageEntityView => ({
  filters: encodeEntityFiltersForSession(view.filters),
  ...(view.odataFilter !== undefined ? { odataFilter: view.odataFilter } : {}),
  filterMode: view.filterMode,
  selectedColumns: view.selectedColumns,
  pageSize: view.pageSize,
});

type DecodedOperand = { value: string | number | bigint | boolean | null; tag?: StorageEntityValue['t'] };

/** Decode one persisted chip operand; `undefined` = not a legal chip value (the caller drops the condition). */
export function decodePersistedFilterOperand(value: unknown): DecodedOperand | undefined {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const { t, v } = value as { t?: unknown; v?: unknown };
    if (typeof t !== 'string' || !EDM_TAGS.has(t)) return undefined;
    try {
      const typed = azureStorageTypedValue(v, t as StorageEntityValue['t']);
      return { value: azureStorageChipValue(typed), tag: typed.t };
    } catch {
      return undefined;
    }
  }
  if (value === null) return { value: null };
  if (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'bigint') return { value };
  if (typeof value === 'number') return Number.isFinite(value) ? { value } : undefined;
  return undefined;
}

// A list item or a range bound is any typed scalar the chip model emits — Boolean included (S4 review D, D7:
// a Boolean list used to drop the whole condition, a Boolean range its second bound). Only null is not one.
const isListItem = (item: DecodedOperand | undefined): item is DecodedOperand & { value: string | number | bigint | boolean } =>
  !!item && item.value !== null;

/** Rebuild one persisted condition (operator already validated) with its operand tags; `undefined` drops it. */
export function decodePersistedCondition(candidate: Record<string, unknown>): FilterCondition | undefined {
  const condition: AzureStorageChipCondition = { operator: candidate.operator as FilterCondition['operator'] };
  const tags: AzureStorageOperandTags = {};
  if ('value' in candidate) {
    if (Array.isArray(candidate.value)) {
      const items = candidate.value.map(decodePersistedFilterOperand);
      if (!items.every(isListItem)) return undefined;
      // The shared chip type has no Boolean list item; an Azure Table chip may hold one (`Flag eq true or Flag eq
      // false`), which the OData emitter types from its own tag like every other operand (review D, D7).
      condition.value = items.map((item) => item.value) as FilterCondition['value'];
      if (items.every((item) => item.tag)) tags.value = items.map((item) => item.tag as StorageEntityValue['t']);
    } else {
      const decoded = decodePersistedFilterOperand(candidate.value);
      if (!decoded) return undefined;
      condition.value = decoded.value;
      if (decoded.tag) tags.value = decoded.tag;
    }
  }
  if ('value2' in candidate) {
    // A bound that cannot be restored drops the condition: half a range would widen the filter silently.
    const decoded = decodePersistedFilterOperand(candidate.value2);
    if (!isListItem(decoded)) return undefined;
    condition.value2 = decoded.value as FilterCondition['value2'];
    if (decoded.tag) tags.value2 = decoded.tag;
  }
  if (tags.value !== undefined || tags.value2 !== undefined) condition.edm = tags;
  return condition;
}
